import React, { useState, createContext, useEffect } from "react";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getDocs, query, where, collection } from "firebase/firestore";
import { db } from "../js/firebase";
import { useAuth0 } from "@auth0/auth0-react";
// import { formatDate } from "../helpers/utils";
import { parseLocalDate, formatDateToString } from "../helpers/dateHelper";
//This profile context handles the user/role signed in and thier functionalities
export const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const [worker, setWorker] = useState(null);
  // const [report, setReport] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [showModel, setShowModel] = useState("all");
  const [show, setShow] = useState(false);
  const [dateRange, setDateRange] = useState("");
  const [showReports, setShowReports] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [organizationImage, setOrganizationImage] = useState(undefined);
  const [organizationNumber, setOrganizationNumber] = useState("");
  const [areaCodeDetails, setAreaCodeDetails] = useState([]);
  const [subscriberAreaCodeChartAllowed, setSubscriberAreaCodeChartAllowed] =
    useState(false);

  // State to store current organization's Firebase ID
  const [organizationFirebaseId, setOrganizationFirebaseId] = useState("");

  // ✅ NEW: Simple boolean flag
  const [viewingAsOrganization, setViewingAsOrganization] = useState(false);

  // ✅ Reset to false on every page load/reload
  useEffect(() => {
    setViewingAsOrganization(false);
  }, []); // Empty dependency array runs once on mount

  const { user, getAccessTokenSilently } = useAuth0();

  // Add this useEffect to ProfileContext.js
  useEffect(() => {
    // When organization changes, update Firebase ID
    if (organization?.id) {
      setOrganizationFirebaseId(organization.id);
    } else if (organization) {
      // If organization object doesn't have id, try to fetch it
      const fetchOrgId = async () => {
        if (organization.name) {
          const q = query(
            collection(db, "organizations"),
            where("name", "==", organization.name),
          );
          const querySnapshot = await getDocs(q);
          querySnapshot.forEach((doc) => {
            setOrganizationFirebaseId(doc.id);
          });
        }
      };
      fetchOrgId();
    }
  }, [organization]); // Add this in ProfileContext

  useEffect(() => {
    const getUserMetadata = async () => {
      const domain = process.env.REACT_APP_AUTH0_DOMAIN;
      setLoading(true); // Start loading before making API calls

      try {
        if (!user) return; // Exit if user is not defined

        // Get access token
        let accessToken;
        try {
          accessToken = await getAccessTokenSilently({
            authorizationParams: {
              audience: `https://${domain}/api/v2/`,
              scope: "openid read:current_user",
            },
            cacheMode: "off",
          });
        } catch (tokenError) {
          console.error("Error getting access token:", tokenError);

          // Handle login_required error
          if (
            tokenError.error === "login_required" ||
            tokenError.oauthError === "login_required"
          ) {
            console.log("Login required - user needs to re-authenticate");
            // Force a fresh login by clearing any cached tokens and reloading
            window.location.href = window.location.origin;
            return;
          }

          // If consent is required, we might need to redirect to login
          if (
            tokenError.error === "consent_required" ||
            tokenError.oauthError === "consent_required"
          ) {
            console.log("Consent required - user needs to re-authenticate");
            // Force a fresh login
            window.location.href = window.location.origin;
            return;
          }

          // Handle organization parameter errors
          if (
            tokenError.error === "invalid_request" &&
            tokenError.error_description?.includes("organization")
          ) {
            console.log(
              "Organization parameter required - check Auth0 configuration",
            );
            // Don't set worker if we can't get proper metadata
            setLoading(false);
            return;
          }

          throw tokenError;
        }

        // Fetch user metadata
        const userDetailsByIdUrl = `https://${domain}/api/v2/users/${user.sub}`;
        const metadataResponse = await fetch(userDetailsByIdUrl, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        // Parse metadata response
        if (!metadataResponse.ok) {
          throw new Error("Failed to fetch user metadata");
        }

        const metaData = await metadataResponse.json();

        const workerInfo = {
          ...metaData.user_metadata,
          email: metaData.email,
        };

        //console.log("Worker info before validation:", workerInfo);

        // Ensure worker has a role and name, if not set, don't set worker (will trigger logout)
        if (!workerInfo.role) {
          console.warn(
            "User role not found in metadata, not setting worker:",
            workerInfo,
          );
          console.log(
            "Available fields in user_metadata:",
            Object.keys(metaData.user_metadata || {}),
          );
          setLoading(false);
          return;
        }

        if (!workerInfo.fullName && !workerInfo.name) {
          console.warn(
            "User name not found in metadata, not setting worker:",
            workerInfo,
          );
          console.log(
            "Available fields in user_metadata:",
            Object.keys(metaData.user_metadata || {}),
          );
          setLoading(false);
          return;
        }

        setWorker(workerInfo);

        // Optionally handle organization data
        getCurrentOrganization(metaData.user_metadata);
      } catch (e) {
        console.error("Error fetching user metadata:", e.message);

        // Handle login_required error
        if (e.error === "login_required" || e.oauthError === "login_required") {
          console.log("Login required - user needs to re-authenticate");
          // Force a fresh login by clearing any cached tokens and reloading
          window.location.href = window.location.origin;
          return;
        }

        // Handle consent required error
        if (
          e.error === "consent_required" ||
          e.oauthError === "consent_required"
        ) {
          console.log(
            "Consent required - user needs to authorize the application",
          );
          // Force a fresh login
          window.location.href = window.location.origin;
          return;
        }
      } finally {
        // Ensure loading is stopped
        setLoading(false);
      }
    };

    // Only call the function if the user is defined
    if (user) {
      getUserMetadata();
    }
  }, [getAccessTokenSilently, user?.sub]);

  const getOrganizationImage = (name) => {
    const storage = getStorage();
    getDownloadURL(ref(storage, `logos/${name}.png`))
      .then((url) => {
        setOrganizationImage(url);
      })
      .catch((error) => {
        // Handle any errors
      });
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const fetchReport = async () => {
    if (organizationNumber) {
      //show data option will be replaced, we will use first day of current month on show current month click
      //start date and or end date will be sent to the backend
      let statsUrl = `${
        process.env.REACT_APP_API_URL
      }/org_stats?twilio_number=${encodeURIComponent(organizationNumber)}`;
      let detailsUrl = `${
        process.env.REACT_APP_API_URL
      }/org_details?twilio_number=${encodeURIComponent(organizationNumber)}`;

      if (showModel === "monthly") {
        var date = new Date();
        var firstDay = formatDateToString(
          parseLocalDate(new Date(date.getFullYear(), date.getMonth(), 1)),
        );
        statsUrl += `&start_date=${firstDay}`;
        detailsUrl += `&start_date=${firstDay}`;
      }
      if (dateRange) {
        statsUrl += `&start_date=${formatDateToString(
          parseLocalDate(dateRange[0]),
        )}&end_date=${formatDateToString(parseLocalDate(dateRange[1]))}`;
        detailsUrl += `&start_date=${formatDateToString(
          parseLocalDate(dateRange[0]),
        )}&end_date=${formatDateToString(parseLocalDate(dateRange[1]))}`;
      }

      var options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };
      try {
        const [result1, result2] = await Promise.all([
          fetch(statsUrl, options),
          fetch(detailsUrl, options),
        ]);

        // Check if both responses are okay
        if (!result1.ok || !result2.ok) {
          throw new Error("One or both API requests failed");
        }
        // Parse both results
        const stats = await result1.json();
        const details = await result2.json();

        // Combine the results into a reports variable
        const reports = {
          stats,
          details,
        };
        // console.log("Report response Data" + JSON.stringify(reports, null, 2));
        return reports;
      } catch (error) {
        console.error("Error fetching reports:", error);
        return null;
      }
    }
  };

  async function getOverallStats(organizationNumbers) {
    let totalsUrl = `${
      process.env.REACT_APP_API_URL
    }/org_totals?twilio_number=${encodeURIComponent(organizationNumbers)}`;

    if (showModel === "monthly") {
      var date = new Date();
      var firstDay = formatDateToString(
        parseLocalDate(new Date(date.getFullYear(), date.getMonth(), 1)),
      );
      totalsUrl += `&start_date=${firstDay}`;
    }
    if (dateRange) {
      totalsUrl += `&start_date=${formatDateToString(
        parseLocalDate(dateRange[0]),
      )}&end_date=${formatDateToString(parseLocalDate(dateRange[1]))}`;
    }

    var options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
        Accept: "application/json",
      },
    };
    try {
      const response = await fetch(totalsUrl, options);

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching reports:", error);
      return null;
    }
  }

  async function getAreaCodeStats(organizationFirebaseId, startDate, endDate) {
    // Check if we have the Firebase ID
    if (!organizationFirebaseId) {
      console.error("❌ No Firebase ID available for organization");
      return null;
    }

    // ✅ Default to current month start → today
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const defaultStart = formatDateToString(parseLocalDate(firstDay));
    const defaultEnd = formatDateToString(parseLocalDate(new Date()));

    // ✅ Use passed dates or fallback to defaults
    const start = startDate
      ? formatDateToString(parseLocalDate(startDate))
      : defaultStart;
    const end = endDate
      ? formatDateToString(parseLocalDate(endDate))
      : defaultEnd;

    // ✅ Build API URL with firebaseId (New endpoint)
    let statsUrl = `${
      process.env.REACT_APP_API_URL
    }/org_calls_by_area_code_report?firebaseId=${encodeURIComponent(
      organizationFirebaseId,
    )}&start_date=${start}&end_date=${end}`;

    if (worker?.role?.includes("admin") && !viewingAsOrganization) {
      statsUrl += `&role=admin`;
    }

    console.log("📊 Area Code Stats API URL →", statsUrl);

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
        Accept: "application/json",
      },
    };

    try {
      const response = await fetch(statsUrl, options);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching area code stats:", error);
      return null;
    }
  }

  async function getProvinceByCountry(
    organizationFirebaseId,
    startDate,
    endDate,
    country,
  ) {
    // Check if we have the Firebase ID
    if (!organizationFirebaseId) {
      console.error("❌ No Firebase ID available for organization");
      return null;
    }

    // ✅ Default to current month start → today
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const defaultStart = formatDateToString(parseLocalDate(firstDay));
    const defaultEnd = formatDateToString(parseLocalDate(new Date()));

    // ✅ Use passed dates or fallback to defaults
    const start = startDate
      ? formatDateToString(parseLocalDate(startDate))
      : defaultStart;
    const end = endDate
      ? formatDateToString(parseLocalDate(endDate))
      : defaultEnd;

    // ✅ Build API URL with firebaseId and country (New endpoint)
    let statsUrl = `${
      process.env.REACT_APP_API_URL
    }/org_calls_by_area_code_report?firebaseId=${encodeURIComponent(
      organizationFirebaseId,
    )}&country_id=${country}&start_date=${start}&end_date=${end}`;

    if (worker?.role?.includes("admin") && !viewingAsOrganization) {
      statsUrl += `&role=admin`;
    }

    console.log("📊 Area Code Stats by Country API URL →", statsUrl);

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
        Accept: "application/json",
      },
    };

    try {
      const response = await fetch(statsUrl, options);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching area code stats by country:", error);
      return null;
    }
  }

  async function getAreaCodeByProvince(
    organizationFirebaseId,
    startDate,
    endDate,
    country,
    province,
  ) {
    // Check if we have the Firebase ID
    if (!organizationFirebaseId) {
      console.error("❌ No Firebase ID available for organization");
      return null;
    }

    // ✅ Default to current month start → today
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const defaultStart = formatDateToString(parseLocalDate(firstDay));
    const defaultEnd = formatDateToString(parseLocalDate(new Date()));

    // ✅ Use passed dates or fallback to defaults
    const start = startDate
      ? formatDateToString(parseLocalDate(startDate))
      : defaultStart;
    const end = endDate
      ? formatDateToString(parseLocalDate(endDate))
      : defaultEnd;

    // ✅ Build API URL with firebaseId, country, and province (New endpoint)
    let statsUrl = `${
      process.env.REACT_APP_API_URL
    }/org_calls_by_area_code_report?firebaseId=${encodeURIComponent(
      organizationFirebaseId,
    )}&country_id=${country}&province_id=${province}&start_date=${start}&end_date=${end}`;

    if (worker?.role?.includes("admin") && !viewingAsOrganization) {
      statsUrl += `&role=admin`;
    }

    console.log("📊 Area Code Stats with Province API URL →", statsUrl);

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
        Accept: "application/json",
      },
    };

    try {
      const response = await fetch(statsUrl, options);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching area code stats with province:", error);
      return null;
    }
  }

  async function getAreaCodeDetails(
    areaCode,
    organizationFirebaseId,
    countryId,
    provinceId,
    dateRange,
  ) {
    if (!organizationFirebaseId) {
      console.error("No Firebase ID available");
      setAreaCodeDetails([]);
      return null;
    }

    try {
      let url = `${
        process.env.REACT_APP_API_URL
      }/org_caller_details_by_area_code_report?firebaseId=${encodeURIComponent(
        organizationFirebaseId.trim(),
      )}
&country_id=${encodeURIComponent(String(countryId).trim())}
&province_id=${encodeURIComponent(String(provinceId).trim())}&area_code=${encodeURIComponent(areaCode.trim())}`;

      if (dateRange?.start) {
        url += `&start_date=${dateRange.start}&end_date=${dateRange.end}`;
      }

      if (worker?.role?.includes("admin") && !viewingAsOrganization) {
        url += `&role=admin`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
        },
      });

      const result = await response.json();

      setAreaCodeDetails(result?.original?.data || []);
      return result?.original?.data;
    } catch (error) {
      console.error("Failed to fetch area code details:", error);
      setAreaCodeDetails([]);
    }
  }

  const getAgentWorkHours = async (startDate, endDate, nameClicked = "") => {
    if ((worker && worker.role === "subscriber") || nameClicked !== "") {
      try {
        // if (worker && worker.role === "subscriber") {
        //   let foundOrganization = await getCurrentOrganization(worker);
        //   if (foundOrganization) {
        //     let fetchNumber = foundOrganization.assignedNumber;
        //     setOrganizationNumber(fetchNumber);
        //   }
        // }
        const orgName = organization?.name ?? "Crossroads";
        let url = `${process.env.REACT_APP_API_URL}/agent-work-hours/detailsbyorganization`;

        // Add query parameters
        const params = new URLSearchParams();

        if (orgName) {
          params.append("organization", orgName);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const options = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: process.env.REACT_APP_API_AUTHORIZATION,
            Accept: "application/json",
          },
        };

        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result;
      } catch (error) {
        console.error("Error fetching agent work hours details:", error);
        return null;
      }
    } else {
      try {
        let url = `${process.env.REACT_APP_API_URL}/agent-work-hours`;

        // Add query parameters
        const params = new URLSearchParams();
        if (startDate) {
          params.append(
            "start_date",
            typeof startDate === "string"
              ? startDate
              : formatDateToString(parseLocalDate(startDate)),
          );
        }
        if (endDate) {
          params.append(
            "end_date",
            typeof endDate === "string"
              ? endDate
              : formatDateToString(parseLocalDate(endDate)),
          );
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const options = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: process.env.REACT_APP_API_AUTHORIZATION,
            Accept: "application/json",
          },
        };

        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result;
      } catch (error) {
        console.error("Error fetching agent work hours:", error);
        return null;
      }
    }
  };

  // NEW: Get volunteers hours for subscriber
  // NEW: Get agent work hours details
  const getAgentWorkHoursDetails = async (startDate, endDate, department) => {
    try {
      let url = `${process.env.REACT_APP_API_URL}/agent-work-hours/details`;

      // Add query parameters
      const params = new URLSearchParams();
      if (startDate) {
        params.append(
          "start_date",
          typeof startDate === "string"
            ? startDate
            : formatDateToString(parseLocalDate(startDate)),
        );
      }
      if (endDate) {
        params.append(
          "end_date",
          typeof endDate === "string"
            ? endDate
            : formatDateToString(parseLocalDate(endDate)),
        );
      }
      if (department) {
        params.append("department", department);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching agent work hours details:", error);
      return null;
    }
  };

  // Get Volunteer hours
  const getVolunteerHours = async (startDate, endDate) => {
    if (!organizationFirebaseId) {
      console.error("❌ No Firebase ID available for organization");
      return null;
    }

    // ✅ Default to current month start → today
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const defaultStart = formatDateToString(parseLocalDate(firstDay));
    const defaultEnd = formatDateToString(parseLocalDate(new Date()));

    // ✅ Use passed dates or fallback to defaults
    const start = startDate
      ? formatDateToString(parseLocalDate(startDate))
      : defaultStart;
    const end = endDate
      ? formatDateToString(parseLocalDate(endDate))
      : defaultEnd;

    // ✅ Build API URL with firebaseId (New endpoint)
    let statsUrl = `${
      process.env.REACT_APP_API_URL
    }/org_volunteer_hours_report?firebaseId=${encodeURIComponent(
      organizationFirebaseId,
    )}&start_date=${start}&end_date=${end}`;

    if (worker?.role?.includes("admin") && !viewingAsOrganization) {
      statsUrl += `&role=admin`;
    }

    console.log("Volunteer Hours API URL →", statsUrl);

    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
        Accept: "application/json",
      },
    };

    try {
      const response = await fetch(statsUrl, options);
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching volunteer hours:", error);
      return null;
    }
  };

  // ✅ UPDATED FUNCTION: getOrgCallMetrics with firebaseId
  // Store controller at the module level (shared between calls)
  let metricsController = null;

  async function getOrgCallMetrics(
    organizationFirebaseId,
    startDate,
    endDate,
    organizationName,
  ) {
    try {
      // 🛑 Cancel previous API call if still running
      if (metricsController) {
        metricsController.abort();
      }

      // 🎯 Create a new controller for this request
      metricsController = new AbortController();
      const { signal } = metricsController;

      // Check if we have the Firebase ID
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        return null;
      }

      // Build API URL with firebaseId parameter
      let metricsUrl = `${
        process.env.REACT_APP_API_URL
      }/org_call_metrics_summary?firebaseId=${encodeURIComponent(
        organizationFirebaseId,
      )}&department=Volunteer`;

      // ✅ Only add role=admin if user is admin AND not viewing as organization
      if (worker?.role?.includes("admin") && !viewingAsOrganization) {
        metricsUrl += `&role=admin`;
      }

      console.log("📊 Org Metrics Summary API URL →", metricsUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
        signal,
      };

      const response = await fetch(metricsUrl, options);
      if (!response.ok)
        throw new Error(`Failed to fetch org call metrics: ${response.status}`);

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("⛔ Previous org metrics API call canceled");
        return null;
      }
      console.error("❌ Error fetching org call metrics summary:", error);
      return null;
    }
  }

  // Similarly for getOrgCallMetricsDetailed
  async function getOrgCallMetricsDetailed(
    organizationFirebaseId,
    startDate,
    endDate,
    organizationName,
  ) {
    try {
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        return null;
      }

      let metricsUrl = `${
        process.env.REACT_APP_API_URL
      }/org_call_metrics_detailed?firebaseId=${encodeURIComponent(
        organizationFirebaseId,
      )}`;

      // ✅ Only add role=admin if user is admin AND not viewing as organization
      if (worker?.role?.includes("admin") && !viewingAsOrganization) {
        metricsUrl += `&role=admin`;
      }

      if (startDate && endDate) {
        const start = formatDateToString(parseLocalDate(startDate));
        const end = formatDateToString(parseLocalDate(endDate));
        metricsUrl += `&start_date=${start}&end_date=${end}`;
      }

      console.log("📊 Org Metrics Detailed API URL →", metricsUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(metricsUrl, options);
      if (!response.ok)
        throw new Error(
          `Failed to fetch detailed call metrics: ${response.status}`,
        );

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching org call metrics detailed:", error);
      return null;
    }
  }

  // ✅ NEW FUNCTION: getCallTypeReport
  async function getCallTypeReport(
    organizationFirebaseId, // Changed from organizationNumber
    type = "full",
    startDate,
    endDate,
  ) {
    try {
      // Check if we have the Firebase ID
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        return null;
      }

      // ✅ Default: current month start → today
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultStart = formatDateToString(parseLocalDate(firstDay));
      const defaultEnd = formatDateToString(parseLocalDate(new Date()));

      // ✅ Use passed dates or fallback to defaults
      const start = startDate
        ? formatDateToString(parseLocalDate(startDate))
        : defaultStart;
      const end = endDate
        ? formatDateToString(parseLocalDate(endDate))
        : defaultEnd;

      // ✅ Build API URL with firebaseId parameter
      let reportUrl = `${
        process.env.REACT_APP_API_URL
      }/org_call_type_report?firebaseId=${encodeURIComponent(
        organizationFirebaseId, // Changed from orgData/twilio_number
      )}`;

      if (startDate && endDate) {
        reportUrl += `&start_date=${start}&end_date=${end}`;
      }

      if (worker?.role?.includes("admin") && !viewingAsOrganization) {
        reportUrl += `&role=admin`;
      }

      console.log("📞 Call Type Report API URL →", reportUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(reportUrl, options);
      if (!response.ok)
        throw new Error(`Failed to fetch call type report: ${response.status}`);

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching call type report:", error);
      return null;
    }
  }

  // ✅ NEW FUNCTION: getDispositionReport
  async function getDispositionReport(
    organizationFirebaseId,
    type = "full",
    startDate,
    endDate,
  ) {
    try {
      // Check if we have the Firebase ID
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        return null;
      }

      // ✅ Default to current month start → today
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultStart = formatDateToString(parseLocalDate(firstDay));
      const defaultEnd = formatDateToString(parseLocalDate(new Date()));

      // ✅ Use passed dates or fallback to defaults
      const start = startDate
        ? formatDateToString(parseLocalDate(startDate))
        : defaultStart;
      const end = endDate
        ? formatDateToString(parseLocalDate(endDate))
        : defaultEnd;

      // ✅ Build base API URL with firebaseId parameter
      let apiUrl = `${
        process.env.REACT_APP_API_URL
      }/org_disposition_report?firebaseId=${encodeURIComponent(
        organizationFirebaseId,
      )}&type=${type}`;

      // ✅ Append start/end date only if both are provided
      if (startDate && endDate) {
        apiUrl += `&start_date=${start}&end_date=${end}`;
      }

      if (worker?.role?.includes("admin") && !viewingAsOrganization) {
        apiUrl += `&role=admin`;
      }

      console.log("📞 Disposition Report API URL →", apiUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(apiUrl, options);
      if (!response.ok)
        throw new Error(
          `Failed to fetch disposition report: ${response.status}`,
        );

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching disposition report:", error);
      return null;
    }
  }

  // Helper functions (make sure these exist in your code)
  function formatDateToString(date) {
    return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD
  }

  function parseLocalDate(dateString) {
    // If it's already a Date object, return it
    if (dateString instanceof Date) return dateString;

    // If it's a string in YYYY-MM-DD format
    if (typeof dateString === "string") {
      const parts = dateString.split("-");
      if (parts.length === 3) {
        return new Date(parts[0], parts[1] - 1, parts[2]);
      }
    }

    // Fallback to current date
    return new Date();
  }
  async function getSalvationRededicationReport(
    organizationNumber,
    type = "full",
    startDate,
    endDate,
  ) {
    try {
      const orgData =
        organizationNumber &&
        organizationNumber !== process.env.REACT_APP_TWILIO_NUMBER
          ? organizationNumber
          : process.env.REACT_APP_TWILIO_NUMBER;

      // ✅ Default to current month start → today
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultStart = formatDateToString(parseLocalDate(firstDay));
      const defaultEnd = formatDateToString(parseLocalDate(new Date()));

      // ✅ Use passed dates or fallback to defaults
      const start = startDate
        ? formatDateToString(parseLocalDate(startDate))
        : defaultStart;
      const end = endDate
        ? formatDateToString(parseLocalDate(endDate))
        : defaultEnd;

      // ✅ Build base API URL
      let apiUrl = `${
        process.env.REACT_APP_API_URL
      }/salvation_rededication_report?twilio_number=${encodeURIComponent(
        orgData,
      )}&type=${type}`;

      // ✅ Append start/end date only if both are provided
      if (startDate && endDate) {
        apiUrl += `&start_date=${start}&end_date=${end}`;
      }

      console.log("📞 Salvation Rededication Report API URL →", apiUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(apiUrl, options);
      if (!response.ok)
        throw new Error(
          `Failed to fetch salvation rededication report: ${response.status}`,
        );

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching salvation rededication report:", error);
      return null;
    }
  }
  const safeParseDate = (dateString) => {
    if (!dateString) return null;
    // const date = new Date(dateString);
    // return isNaN(date.getTime()) ? null : date;

    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  // ✅ UPDATED FUNCTION: getAgeGenderReport - Uses Firebase ID
  async function getAgeGenderReport(
    organizationFirebaseId,
    type = "full",
    startDate,
    endDate,
  ) {
    try {
      // Check if we have the Firebase ID
      if (!organizationFirebaseId) {
        console.error("❌ No Firebase ID available for organization");
        return null;
      }

      // ✅ Default to current month start → today
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultStart = formatDateToString(parseLocalDate(firstDay));
      const defaultEnd = formatDateToString(parseLocalDate(new Date()));

      // ✅ Use passed dates or fallback to defaults
      const start = startDate
        ? formatDateToString(parseLocalDate(startDate))
        : defaultStart;
      const end = endDate
        ? formatDateToString(parseLocalDate(endDate))
        : defaultEnd;

      // ✅ Build base API URL with firebaseId parameter
      let apiUrl = `${
        process.env.REACT_APP_API_URL
      }/org_age_gender_report?firebaseId=${encodeURIComponent(organizationFirebaseId)}`;

      // ✅ Append start/end date if provided
      if (startDate && endDate) {
        apiUrl += `&start_date=${start}&end_date=${end}`;
      } else {
        // If no dates provided, use default dates
        apiUrl += `&start_date=${defaultStart}&end_date=${defaultEnd}`;
      }

      if (worker?.role?.includes("admin") && !viewingAsOrganization) {
        apiUrl += `&role=admin`;
      }

      console.log("📊 Age/Gender Report API URL →", apiUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(apiUrl, options);
      if (!response.ok)
        throw new Error(
          `Failed to fetch age/gender report: ${response.status}`,
        );

      const result = await response.json();
      console.log(result);

      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching age/gender report:", error);
      return null;
    }
  }

  async function getPrayerRequests(organizationNumber, startDate, endDate) {
    try {
      const orgData =
        organizationNumber &&
        organizationNumber !== process.env.REACT_APP_TWILIO_NUMBER
          ? organizationNumber
          : process.env.REACT_APP_TWILIO_NUMBER;

      // ✅ Default to current month start → today
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultStart = formatDateToString(parseLocalDate(firstDay));
      const defaultEnd = formatDateToString(parseLocalDate(new Date()));

      // ✅ Use passed dates or fallback to defaults
      const start = startDate
        ? formatDateToString(parseLocalDate(startDate))
        : defaultStart;
      const end = endDate
        ? formatDateToString(parseLocalDate(endDate))
        : defaultEnd;

      // ✅ Build base API URL
      let apiUrl = `${
        process.env.REACT_APP_API_URL
      }/prayer-request/metrics?twilio_number=${encodeURIComponent(orgData)}`;

      // ✅ Append start/end date only if both are provided
      if (startDate && endDate) {
        apiUrl += `&start_date=${start}&end_date=${end}`;
      }

      console.log("prayer request Report API URL →", apiUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(apiUrl, options);
      if (!response.ok)
        throw new Error(
          `Failed to fetch prayer request report: ${response.status}`,
        );

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching prayer request report:", error);
      return null;
    }
  }

  async function getVoicemailSummary(organizationNumber, startDate, endDate) {
    try {
      const orgData =
        organizationNumber &&
        organizationNumber !== process.env.REACT_APP_TWILIO_NUMBER
          ? organizationNumber
          : process.env.REACT_APP_TWILIO_NUMBER;

      // ✅ Default to current month start → today
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultStart = formatDateToString(parseLocalDate(firstDay));
      const defaultEnd = formatDateToString(parseLocalDate(new Date()));

      // ✅ Use passed dates or fallback to defaults
      const start = startDate
        ? formatDateToString(parseLocalDate(startDate))
        : defaultStart;
      const end = endDate
        ? formatDateToString(parseLocalDate(endDate))
        : defaultEnd;

      // ✅ Build base API URL
      let apiUrl = `${
        process.env.REACT_APP_API_URL
      }/voicemail-recording/report/summary?twilio_number=${encodeURIComponent(
        orgData,
      )}`;

      // ✅ Append start/end date only if both are provided
      if (startDate && endDate) {
        apiUrl += `&start_date=${start}&end_date=${end}`;
      }

      console.log("Voicemail Summary Report API URL →", apiUrl);

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(apiUrl, options);
      if (!response.ok)
        throw new Error(
          `Failed to fetch voicemail recording summary: ${response.status}`,
        );

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching voicemail summary report:", error);
      return null;
    }
  }

  async function getResourceReport(
    organizationNumber,
    type = "digital", // Default to digital
    startDate,
    endDate,
  ) {
    try {
      const orgData =
        organizationNumber &&
        organizationNumber !== process.env.REACT_APP_TWILIO_NUMBER
          ? organizationNumber
          : process.env.REACT_APP_TWILIO_NUMBER;

      // Default to current month start → today
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultStart = formatDateToString(parseLocalDate(firstDay));
      const defaultEnd = formatDateToString(parseLocalDate(new Date()));

      // Use passed dates or fallback to defaults
      const start = startDate
        ? formatDateToString(parseLocalDate(startDate))
        : defaultStart;
      const end = endDate
        ? formatDateToString(parseLocalDate(endDate))
        : defaultEnd;

      // Build base API URL with type parameter
      let apiUrl = `${
        process.env.REACT_APP_API_URL
      }/resource_report?type=${type}&twilio_number=${encodeURIComponent(
        orgData,
      )}`;

      // Append start/end date only if both are provided
      if (startDate && endDate) {
        apiUrl += `&start_date=${start}&end_date=${end}`;
      }

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const response = await fetch(apiUrl, options);
      if (!response.ok)
        throw new Error(`Failed to fetch resource report: ${response.status}`);

      const result = await response.json();
      return result?.original?.data || result;
    } catch (error) {
      console.error("❌ Error fetching resource report:", error);
      return null;
    }
  }

  async function getOrganizationsReport() {
    // If a specific organization number is already set, use that regardless of role
    // This allows admins to view selected organization data
    if (
      organizationNumber &&
      organizationNumber !== process.env.REACT_APP_TWILIO_NUMBER
    ) {
      return fetchReport();
    }

    // For subscriber role, get their own organization
    if (worker && worker.role === "subscriber") {
      let foundOrganization = await getCurrentOrganization(worker);
      if (foundOrganization) {
        let fetchNumber = foundOrganization.assignedNumber;
        setOrganizationNumber(fetchNumber);
      }
    }
    // For super_admin, admin, prayer_centre_admin, prayer_centre_lead roles
    // Use the default number only if no specific organization is selected
    else if (
      worker &&
      (worker.role === "super_admin" ||
        worker.role === "admin" ||
        worker.role === "prayer_centre_admin" ||
        worker.role === "prayer_centre_lead")
    ) {
      if (!organizationNumber) {
        setOrganizationNumber(process.env.REACT_APP_TWILIO_NUMBER);
      }
    }

    return fetchReport();
  }

  // Updated getCurrentOrganization function to also set Firebase ID
  const getCurrentOrganization = async (worker) => {
    let foundOrganization = undefined;
    if (worker.organizationName) {
      const q = query(
        collection(db, "organizations"),
        where("name", "==", worker.organizationName),
      );
      const querySnapshot = await getDocs(q);
      let firstFlag = true;
      querySnapshot.forEach((doc) => {
        if (firstFlag) {
          firstFlag = false;
          // Spread data first so a stored `id` field cannot overwrite the real doc id
          foundOrganization = {
            ...doc.data(),
            id: doc.id,
          };
          setOrganization(foundOrganization);

          // Set both the assigned number and Firebase ID
          let fetchNumber = foundOrganization.assignedNumber;
          setOrganizationNumber(fetchNumber);
          setOrganizationFirebaseId(doc.id); // Store the Firebase ID
        }
      });
    }
    getOrganizationImage(foundOrganization?.name);

    if (worker && worker.role === "subscriber" && foundOrganization) {
      let fetchNumber = foundOrganization.assignedNumber;
      if (
        foundOrganization?.is_premium &&
        subscriberAreaCodeChartAllowed !== foundOrganization.is_premium
      ) {
        setSubscriberAreaCodeChartAllowed(true);
      }
    }

    return foundOrganization;
  };

  const formatData = (report) => {
    var labels = [];
    var values = [];
    report.forEach((obj) => {
      labels.push(Object.values(obj)[0]);
      values.push(Object.values(obj)[1]);
    });
    return [labels, values];
  };

  const chartReportData = (report, label, isformatted) => {
    var labels, values;
    if (!isformatted) {
      [labels, values] = formatData(report);
    } else {
      [labels, values] = report;
    }
    if (label == "Age Range of Callers") {
      var adultIndex = labels.indexOf("Adult");
      labels[adultIndex] = "Adult (26-40)";

      var middleIndex = labels.indexOf("Middle Aged");
      labels[middleIndex] = "Middle Aged (41-58)";

      var seniorsIndex = labels.indexOf("Seniors");
      labels[seniorsIndex] = "Seniors (59+)";

      var youngIndex = labels.indexOf("Young Adult");
      labels[youngIndex] = "Young Adult (20-25)";

      var teenIndex = labels.indexOf("Teenager");
      labels[teenIndex] = "Teenager (13-19)";

      var childIndex = labels.indexOf("Child");
      labels[childIndex] = "Child (< 11 yrs)";
    }
    return {
      labels: labels,
      datasets: [
        {
          label: label,
          data: values,
          backgroundColor: [
            "#FF7234",
            "#EC3310",
            "#2DB3C0",
            "#ADCFD4",
            "#DE735E",
            "#00323B",
            "#FFB0A2",
            "#8DDCE8",
            "#048097",
            "#77B3B8",
            "#BFE9EE",
            "#E6FDFF",
            "#FAC6B1",
            "#FFEEE9",
            "#FCD5C9",
            "#F5F5F5",
          ],
          hoverOffset: 4,
        },
      ],
    };
  };

  //format the array for it to be displayed in the chart correctly
  // const formatArray = (report, label) => {
  //     if(report){
  //         const labelCount = {};
  //         report.forEach(element => {
  //             if(element){
  //                 const labels = element.split(',').map(label => label.split('-')[0].trim());
  //                 // Use a Set to track unique labels in this item
  //                 const uniqueLabels = new Set();

  //                 labels.forEach(label => {
  //                     if (label) {
  //                         uniqueLabels.add(label);
  //                     }
  //                 });
  //                 uniqueLabels.forEach(label => {
  //                     // Increment count for each base label
  //                     labelCount[label] = (labelCount[label] || 0) + 1;
  //                 });
  //             }
  //         });

  //         var callTypeLabels = Object.keys(labelCount)
  //         var callTypeValues = Object.values(labelCount)

  //         return [callTypeLabels, callTypeValues ]
  //     }
  // }

  const formatObj = (object, label) => {
    return Object.entries(object).map(([key, value]) => ({
      [label]: key,
      total: value,
    }));
  };
  const countTypesBreakdowns = (array, baseLabel) => {
    const breakdownCount = {};
    const filteredArray = array.filter((item) => item.includes(baseLabel));

    filteredArray.forEach((item) => {
      // Split the item into individual labels
      if (item) {
        item.split(",").forEach((label) => {
          const [labelBase, breakdown] = label
            .split("-")
            .map((part) => part.trim());

          if (labelBase === baseLabel && breakdown) {
            breakdownCount[breakdown] = (breakdownCount[breakdown] || 0) + 1;
          }
        });
      }
    });
    var breakdownLabel = Object.keys(breakdownCount);
    var breakdownValues = Object.values(breakdownCount);
    return [breakdownLabel, breakdownValues];
  };

  const evenlyDistribute = (dataset, total_calls) => {
    //get dataset total
    let datasetTotal = 0;
    dataset.forEach((item) => {
      datasetTotal += item.total;
    });
    //get the number of missingCalls
    let missingCalls = total_calls - datasetTotal;
    if (missingCalls >= 0) {
      let updatedDisposition = dataset.map((item) => {
        let proportionDispotion =
          Math.round(item.total / datasetTotal) * missingCalls;
        item.total += proportionDispotion;
        return item;
      });
      return updatedDisposition;
    }
    return dataset;
  };

  const breakedownReports = (reportObj, categories) => {
    // Initialize the result object with keys for each prefix and 'otherReports'
    const groupedReports = {};
    const otherReport = {};

    // Initialize the categories in the groupedReports
    categories.forEach((category) => {
      groupedReports[category] = {};
    });

    // Iterate through each entry in the report
    for (const [key, value] of Object.entries(reportObj)) {
      let addedToGroup = false;

      // Check if the key starts with one of the base categories
      categories.forEach((category) => {
        if (key.startsWith(category) && key !== category) {
          const subcategory = key.replace(`${category} -`, "").trim();
          groupedReports[category][subcategory] = value;
          addedToGroup = true;
        }
      });

      // If the key doesn't start with any of the categories, add it to otherReport
      if (!addedToGroup && categories.includes(key)) {
        otherReport[key] = value;
      } else if (!categories.some((category) => key.startsWith(category))) {
        otherReport[key] = value;
      }
    }
    return { groupedReports, otherReport };
  };

  const generateReportsCharts = (report, total_calls) => {
    if (report) {
      var callType = breakedownReports(report.call_type_report, [
        "Financial Needs",
        "Family Concerns",
        "Healing",
        "Emotional Needs",
      ]);

      var genderData = chartReportData(
        report.gender_report,
        "Gender of Callers",
      );
      var ageRangeData = chartReportData(
        report.age_range_report,
        "Age Range of Callers",
      );
      var dispositionData = chartReportData(
        evenlyDistribute(report.disposition_report, total_calls),
        "Disposition",
      );
      var callTypeData = chartReportData(
        formatObj(callType.otherReport, "call_type"),
        "Type Of Calls",
      );

      var finalcialNeedsData = chartReportData(
        formatObj(
          callType.groupedReports["Financial Needs"],
          "Financial Needs",
        ),
        "Financial Needs Breakdown",
      );
      var healingsData = chartReportData(
        formatObj(callType.groupedReports["Healing"], "Healing"),
        "Healing Breakdown",
      );
      var familyConcernsData = chartReportData(
        formatObj(
          callType.groupedReports["Family Concerns"],
          "Family Concerns",
        ),
        "Family Concerns Breakdown",
      );
      var emotionalNeedsData = chartReportData(
        formatObj(
          callType.groupedReports["Emotional Needs"],
          "Emotional Needs",
        ),
        "Emotional Needs Breakdown",
      );

      var callTypeBreakdownData = [
        finalcialNeedsData,
        healingsData,
        familyConcernsData,
        emotionalNeedsData,
      ];

      // var followUpTypeData = formatArray(report[0].follow_up_type_report[0], "Type Of Follow Up");

      var digitalResourcesData = chartReportData(
        formatObj(report.digital_resource_report, "resource"),
        "Digital Resource",
      );
      var physicalResourcesData = chartReportData(
        formatObj(report.physical_resource_report, "resource"),
        "Physical Resource ",
      );
    }
    return [
      dispositionData,
      callTypeData,
      callTypeBreakdownData,
      ageRangeData,
      genderData,
      digitalResourcesData,
      physicalResourcesData,
    ];
  };

  return (
    <ProfileContext.Provider
      value={{
        setWorker,
        worker,
        getOrganizationsReport,
        show,
        handleClose,
        handleShow,
        showModel,
        setShowModel,
        organizationImage,
        loading,
        organization,
        setOrganization,
        setDateRange,
        dateRange,
        showReports,
        setShowReports,
        setOrganizationImage,
        evenlyDistribute,
        generateReportsCharts,
        organizationNumber,
        setOrganizationNumber,
        getOverallStats,
        areaCodeDetails,
        getAreaCodeDetails,
        getAreaCodeStats,
        getProvinceByCountry,
        getAreaCodeByProvince,
        subscriberAreaCodeChartAllowed,
        setSubscriberAreaCodeChartAllowed,
        getAgentWorkHours,
        getAgentWorkHoursDetails,
        getCurrentOrganization,
        getOrgCallMetrics,
        getOrgCallMetricsDetailed,
        getCallTypeReport,
        getDispositionReport,
        getAgeGenderReport,
        getPrayerRequests,
        getVoicemailSummary,

        getResourceReport,
        organizationFirebaseId, // Export the Firebase ID

        getSalvationRededicationReport,

        // ✅ Expose the flag
        viewingAsOrganization,
        setViewingAsOrganization,

        // getVolunteerHours,
        getVolunteerHours,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
